import { useAppDispatch, useAppSelector } from '@/app/redux/hooks';
import { useAssessments } from '@/app/redux/hooks/assessments';
import { useStudents } from '@/app/redux/hooks/students';
import { setAttemptedProFeature } from '@/app/redux/slices/modalsSlice';
import { selectShareLink } from '@/app/redux/slices/shareLinkSlice';
import { Assessment, AssessmentStudent } from '@/common/types/assessment';
import {
  PlacementTest,
  PlacementTestResult,
} from '@/common/types/placementTest';
import { Activity } from '@/common/types/session';
import { useIsFeatureEnabled } from '@/common/ui/FeatureGate';
import ShareLinkDialogue from '@/common/ui/modals/ShareLinkDialogue';
import { ProIcon } from '@/common/ui/UpgradeButton';
import AssessmentStudentRow from '@/dashboard/Assessment/AssessmentStudentRow';
import PlacementTestStudentRow from '@/dashboard/Assessment/PlacementTestStudentRow';
import ActivityDialog from '@/dashboard/LearnerView/ActivityFeed/ActivityDialog';
import PlacementTestDialogue from '@/dashboard/LearnerView/ActivityFeed/PlacementTestDialogue';
import api from '@/db/apiClient';
import { PencilLineIcon, PlusIcon } from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@shadcn/components/table';
import { useEffect, useMemo, useState } from 'react';

type AssessmentStudentDetails = AssessmentStudent & {
  studentId: string;
  assessmentId: string;
};

interface LearnerAssessmentsProps {
  studentId: string;
  organisationId: string;
  onCreateAssessment?: () => void;
}
export default function LearnerAssessments({
  studentId,
  organisationId,
  onCreateAssessment,
}: LearnerAssessmentsProps) {
  const dispatch = useAppDispatch();
  const isFeatureEnabled = useIsFeatureEnabled();
  const { studentsById } = useStudents();

  const { assessments: allAssessments, placementTests: allPlacementTests } =
    useAssessments();
  const stateAssessments = useMemo(
    () =>
      allAssessments.filter((a) => Object.keys(a.students).includes(studentId)),
    [allAssessments, studentId],
  );
  const statePlacementTests = useMemo(
    () => allPlacementTests.filter((t) => t.studentId === studentId),
    [allPlacementTests, studentId],
  );
  const { shareLink } = useAppSelector(selectShareLink);

  const [isLoading, setLoading] = useState(false);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [placementTests, setPlacementTests] = useState<PlacementTest[]>([]);
  const [activitiesByAssessmentId, setActivitiesByAssessmentId] = useState<
    Record<string, Activity | undefined>
  >({});
  const [selectedResult, setSelectedResult] = useState<Activity | null>(null);
  const [selectedPlacementTestResult, setSelectedPlacementTestResult] =
    useState<PlacementTestResult | null>(null);
  const [prevResult, setPrevResult] = useState<Activity | null>(null);
  const [shareLinkDialogueOpen, setShareLinkDialogueOpen] = useState(false);

  useEffect(() => {
    (async () => {
      if (api.asShareLink) {
        try {
          setLoading(true);
          const assessments = await api.asShareLink.getAssessmentsForStudent(
            organisationId,
            studentId,
          );
          setAssessments(assessments);
          const placementTests =
            await api.asShareLink.getPlacementTestsForStudent(
              organisationId,
              studentId,
            );
          setPlacementTests(placementTests);
        } catch (e) {
          console.error('Error getting share link details:', e);
        } finally {
          setLoading(false);
        }
      } else {
        setAssessments(stateAssessments);
        setPlacementTests(statePlacementTests);
      }
    })();
  }, [
    studentId,
    organisationId,
    shareLink,
    stateAssessments,
    statePlacementTests,
  ]);

  const assessmentIds = useMemo(
    () => assessments.map((a) => a.id),
    [assessments],
  );

  const assessmentsById = useMemo(() => {
    return assessments.reduce<Record<string, Assessment>>((acc, assessment) => {
      acc[assessment.id] = assessment;
      return acc;
    }, {});
  }, [assessments]);

  const sortedPlacementTests = useMemo(() => {
    return [...placementTests]
      .sort((a, b) => b.createdAt - a.createdAt)
      .filter(
        (a) =>
          !!studentsById.get(a.studentId) ||
          (shareLink && a.studentId === studentId),
      );
  }, [placementTests, studentsById, shareLink, studentId]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const service = api.asShareLink ?? api.asEducator;
      const activities = (
        await Promise.all(
          assessmentIds.map((id) =>
            service.getActivitiesForAssessment(organisationId, id),
          ),
        )
      ).flat();
      const activitiesByAssessmentId = activities.reduce<
        Record<string, Activity | undefined>
      >((acc, activity) => {
        // assessmentId won't be missing but gotta check anyway
        if (!activity.assessmentId) return acc;

        // don't include partial completions
        if (!activity.endedAt) return acc;

        // only v3+ activities will be attached to assessments
        if (activity.version === 1 || activity.version === 2) return acc;

        return {
          ...acc,
          [activity.assessmentId]: activity,
        };
      }, {});
      setActivitiesByAssessmentId(activitiesByAssessmentId);
      setLoading(false);
    })();
  }, [organisationId, assessmentIds]);

  const assessmentDetailsForStudent: AssessmentStudentDetails[] = useMemo(
    () =>
      assessments.reduce<AssessmentStudentDetails[]>((acc, assessment) => {
        const student = assessment.students[studentId];
        if (!student) return acc;
        return [
          ...acc,
          {
            studentId,
            ...student,
            assessmentId: assessment.id,
          },
        ];
      }, []),
    [studentId, assessments],
  );

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="px-6">Created</TableHead>
          <TableHead className="pl-4">Status</TableHead>
          <TableHead className="pl-4">Content</TableHead>
          <TableHead className="pl-4">Control Type</TableHead>
          <TableHead className="pl-4">Placement</TableHead>
          <TableHead className="px-6">Action</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {assessmentDetailsForStudent.map((details, i) => (
          <AssessmentStudentRow
            key={i}
            studentId={details.studentId}
            assessment={assessmentsById[details.assessmentId]}
            assessmentStudent={details}
            isLoading={isLoading}
            hasResult={!!activitiesByAssessmentId[details.assessmentId]}
            onViewResult={() => {
              const activity = activitiesByAssessmentId[details.assessmentId];
              setSelectedResult(activity ?? null);
            }}
            inLearnerView
            isShareLink={!!shareLink}
          />
        ))}
        {sortedPlacementTests.map((placementTest) => (
          <PlacementTestStudentRow
            key={placementTest.id}
            placementTest={placementTest}
            onViewResult={(result) => setSelectedPlacementTestResult(result)}
          />
        ))}
        {assessmentDetailsForStudent.length + sortedPlacementTests.length ===
          0 && (
          <TableRow className="hover:bg-white">
            <TableCell colSpan={6}>
              <div className="min-h-[60vh] flex flex-col items-center justify-center gap-6">
                <PencilLineIcon
                  size={80}
                  className="text-neutral-400"
                  weight="thin"
                />
                <div className="flex flex-col items-center gap-2">
                  <h3 className="text-lg font-medium text-neutral-900">
                    No assessments yet
                  </h3>
                  <p className="text-sm text-neutral-500 max-w-[400px] text-center">
                    This learner hasn't completed any assessments yet. Results
                    will appear here once they've been assigned to an assessment
                    and played through it.
                  </p>
                </div>
                {!shareLink && (
                  <Button
                    onClick={() =>
                      isFeatureEnabled('assessments')
                        ? onCreateAssessment?.()
                        : dispatch(setAttemptedProFeature('Assessments'))
                    }
                    className="gap-2"
                  >
                    <PlusIcon size={16} />
                    Create Assessment
                    {!isFeatureEnabled('assessments') && <ProIcon />}
                  </Button>
                )}
              </div>
            </TableCell>
          </TableRow>
        )}
      </TableBody>

      <ActivityDialog
        activity={selectedResult}
        studentId={studentId}
        onClose={() => setSelectedResult(null)}
        onShare={() => {
          setPrevResult(selectedResult);
          setSelectedResult(null);
          setShareLinkDialogueOpen(true);
        }}
      />

      <PlacementTestDialogue
        result={selectedPlacementTestResult}
        onClose={() => setSelectedPlacementTestResult(null)}
      />

      {prevResult && (
        <ShareLinkDialogue
          open={shareLinkDialogueOpen}
          onClose={() => {
            setShareLinkDialogueOpen(false);
            setSelectedResult(prevResult);
            setPrevResult(null);
          }}
          studentId={studentId}
          type="activity"
          resourceId={prevResult.id}
        />
      )}
    </Table>
  );
}
